import { transactionMonthKey, reportCategoryId, reportTransactionType } from '../../utils/reportTransactions';
import { useMemo } from 'react';
import { getCurrentNZMonthString } from '../../utils/nzDateUtils';

/**
 * Custom hook to compute budget data from transactions and categories.
 * Returns { months, incomeCategories, expenseCategories, monthlySummary }
 */
export default function useBudgetData(transactions, categories) {
  return useMemo(() => {
    // Collect all months present in transactions
    const monthsSet = new Set();
    transactions.forEach(transaction => {
      const month = transactionMonthKey(transaction.date);
      monthsSet.add(month);
    });
    
    // Always include the current NZ month, even if no transactions exist for it
    const currentNZMonth = getCurrentNZMonthString();
    monthsSet.add(currentNZMonth);
    
    const months = Array.from(monthsSet).sort((a, b) => {
      const [aMonth, aYear] = a.split('-');
      const [bMonth, bYear] = b.split('-');
      const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      if (aYear !== bYear) return aYear - bYear;
      return monthOrder.indexOf(aMonth) - monthOrder.indexOf(bMonth);
    });

    const incomeCategories = {};
    const expenseCategories = {};
    const monthlySummary = {};
    months.forEach(month => {
      monthlySummary[month] = { income: 0, expenses: 0 };
    });

    transactions.forEach(transaction => {
      const month = transactionMonthKey(transaction.date);
      const amount = transaction.amount;
      const rawCategory = reportCategoryId(transaction, categories);
      const categoryType = reportTransactionType(transaction, categories);
      if (!categoryType) return;
      if ((categoryType === 'Income') || (categoryType === 'Mixed' && amount > 0)) {
        if (!incomeCategories[rawCategory]) {
          incomeCategories[rawCategory] = {};
          months.forEach(m => { incomeCategories[rawCategory][m] = 0; });
        }
        incomeCategories[rawCategory][month] += amount;
        monthlySummary[month].income += amount;
      } else if ((categoryType === 'Expense') || (categoryType === 'Mixed' && amount < 0)) {
        if (!expenseCategories[rawCategory]) {
          expenseCategories[rawCategory] = {};
          months.forEach(m => { expenseCategories[rawCategory][m] = 0; });
        }
        expenseCategories[rawCategory][month] += -amount; // Store expenses as positive numbers
        monthlySummary[month].expenses += -amount; // Store expenses as positive numbers
      }
    });

    return {
      months,
      incomeCategories,
      expenseCategories,
      monthlySummary
    };
  }, [transactions, categories]);
} 
